import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppButton,
  Box,
  Dropdown,
  NotVisibleICount,
  Option,
  OptionKey,
  Preloader,
  SearchInput,
  Text,
  ThinSearchIcon,
  useWatch,
} from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import CheckboxDropdownGroup from '@/modules/role/CheckboxDropdownGroup';
import { useRolesList } from '@/modules/role/hooks';
import { IPermission, NO_ACCESS_ROLE_ID, RoleLevel, SYNTHETIC_SELF_ID } from '@/modules/role/role.types';
interface Props {
  isAllowOtherMode?: boolean;
  isEditMode?: boolean;
  errorText?: string;
  values: OptionKey[];
  onChangeValues: (values: OptionKey[]) => void;
}

const StyledList = styled.ul`
  list-style: inherit;
  padding-left: ${({ theme }) => theme.spacings.l};
`;
const AllowRolesDropdown: React.FC<Props> = ({
  errorText,
  isAllowOtherMode,
  isEditMode,
  values,
  onChangeValues,
}) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const [currentValues, setCurrentValues] = useState<OptionKey[]>(values || []);
  const [currentSearch, setCurrentSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const roleLevel = useWatch({
    name: 'level',
  }) as RoleLevel;
  const organizationId = useWatch({
    name: 'organizationId',
  }) as string;
  const roleName = useWatch({
    name: 'name',
    disabled: isAllowOtherMode,
  }) as RoleLevel;
  const roleId = useWatch({
    name: 'id',
  });

  const [previousRoleLevel, setPreviousRoleLevel] = useState<null | RoleLevel>(roleLevel || null);

  useEffect(() => {
    if (previousRoleLevel && roleLevel && previousRoleLevel !== roleLevel) {
      onChangeValues([]);
      setCurrentValues([]);
    }
    setPreviousRoleLevel(roleLevel);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleLevel]);
  useEffect(() => {
    if (expanded && searchRef.current) {
      searchRef.current.focus();
    }
  }, [expanded]);
  const currentRoleLevels = useMemo(() => {
    if (roleId === NO_ACCESS_ROLE_ID) {
      return [RoleLevel.Elevate, RoleLevel.Global];
    }
    if (isAllowOtherMode) {
      if (roleLevel === RoleLevel.Elevate) {
        return [RoleLevel.Elevate];
      }
      if (roleLevel === RoleLevel.Global) {
        return [RoleLevel.Global, RoleLevel.Elevate];
      }
      if (roleLevel === RoleLevel.Partner) {
        return [RoleLevel.Partner, RoleLevel.Elevate];
      }
    }
    if (roleLevel === RoleLevel.Elevate) {
      return [RoleLevel.Elevate, RoleLevel.Global, RoleLevel.Partner];
    }
    if (roleLevel === RoleLevel.Global) {
      return [RoleLevel.Global];
    }
    if (roleLevel === RoleLevel.Partner) {
      return [RoleLevel.Partner, RoleLevel.Global];
    }
    return [];
  }, [roleId, isAllowOtherMode, roleLevel]);

  const currentSyntheticItem = useMemo<IPermission | undefined>(() => {
    if (!isAllowOtherMode && !roleId && roleName && roleLevel) {
      const item: IPermission = {
        key: SYNTHETIC_SELF_ID,
        id: SYNTHETIC_SELF_ID,
        name: `${roleName || ''} (this role)`,
        description: '',
      };
      return item;
    }
    return undefined;
  }, [isAllowOtherMode, roleId, roleLevel, roleName]);
  const { roleLevelOptions, roleLevelGroups, isLoading, roleLevelOptionsMap } = useRolesList({
    permissions: !isAllowOtherMode ? [] : [22, 23],
    filterIds: isAllowOtherMode ? [roleId] : undefined,
    organizationId: roleLevel === RoleLevel.Partner && !isAllowOtherMode ? organizationId : undefined,
    currentRoleId: roleId,
    roleLevel: roleLevel,
    syntheticElement: currentSyntheticItem,
    searchString: currentSearch,
    roleLevels: currentRoleLevels,
  });

  const titleOptions = useMemo(() => {
    if (values.length) {
      const filteredOptions = [
        ...(currentSyntheticItem ? [currentSyntheticItem] : []),
        ...roleLevelOptions,
      ].filter((item) => values.includes(item.id));
      const result: Option[] = filteredOptions.map((item) => ({
        key: item.id,
        title: item.name,
        value: item.name,
      }));
      return result;
    }
    return null;
  }, [currentSyntheticItem, roleLevelOptions, values]);

  const handleCancel = useCallback(() => {
    setExpanded(false);

    setCurrentValues(values);
  }, [values]);

  const handleApply = useCallback(() => {
    onChangeValues(currentValues);
    setExpanded(false);
  }, [currentValues, onChangeValues]);
  const titleKeys = useMemo(() => {
    if (!titleOptions) {
      return '';
    }
    return titleOptions.map((item) => item.title).join(',');
  }, [titleOptions]);
  const formattedPermissions = useMemo(() => {
    if (_.isEmpty(roleLevelOptionsMap)) {
      return [];
    }
    return (
      values
        ?.filter((id) => roleLevelOptionsMap[id as string])
        .map((id) => roleLevelOptionsMap[id as string]) || []
    );
  }, [values, roleLevelOptionsMap]);

  const keys = useMemo(() => {
    if (_.isEmpty(roleLevelOptionsMap)) {
      return [];
    }
    return (
      values
        ?.filter((id) => roleLevelOptionsMap[id as string])
        .map((id) => roleLevelOptionsMap[id as string].id) || []
    );
  }, [values, roleLevelOptionsMap]);

  useEffect(() => {
    if (isLoading) return;
    if (!_.isEmpty(roleLevelOptionsMap) && !_.isEmpty(keys)) {
      onChangeValues(keys);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleLevelOptionsMap, isLoading]);

  if (!isEditMode) {
    return (
      <StyledList>
        {formattedPermissions.map((permission) => (
          <li data-testid={`allow-create-another-role_${permission.key}`} key={permission.key}>
            <Text size="medium">{permission?.name}</Text>
          </li>
        ))}
      </StyledList>
    );
  }
  return (
    <Box data-testid="allow-roles_wrapper">
      <Dropdown
        placeholder="Select roles"
        isClickOutside={false}
        testId="allow-roles"
        expanded={expanded}
        errorText={errorText}
        icon={<ThinSearchIcon color="iconPrimary" />}
        title={
          titleOptions ? (
            <Box margin={{ right: 'l' }}>
              <NotVisibleICount title={titleKeys} key={titleKeys} options={titleOptions}></NotVisibleICount>
            </Box>
          ) : undefined
        }
        onSetExpanded={() => setExpanded(true)}
        contentStyles={{
          elevation: 'active',
          style: {
            left: '',
            right: '0px',
            minWidth: '340px',
          },
        }}
      >
        <Box direction="column">
          <Box pad="m" direction="column">
            <SearchInput
              inputRef={searchRef}
              value={currentSearch}
              testId="allow-roles_dropdown_search"
              defaultValue={currentSearch}
              placeholder="Search"
              onChange={setCurrentSearch}
            />
          </Box>

          <Box
            pad={{ top: 'xs', left: 'm', right: 'm', bottom: 'm' }}
            border={{ side: 'horizontal', size: 'small', color: 'border1' }}
            style={{ overflow: 'auto' }}
            height="290px"
          >
            <div>
              <Box>
                <Box direction="column">
                  {roleLevelGroups.map((group) => (
                    <CheckboxDropdownGroup
                      isSelectedMode={false}
                      defaultExpanded
                      key={group.id}
                      defaultValues={values}
                      id={group.id}
                      name={group.name}
                      values={currentValues}
                      onChangeValues={setCurrentValues}
                      options={group.options}
                    />
                  ))}
                </Box>
              </Box>
              {isLoading && <Preloader testId="allow-roles_preloader" />}
            </div>
          </Box>

          <Box pad="m" direction="row">
            <Box margin={{ right: 'spacing6' }}>
              <AppButton
                testId="allow-roles_dropdown_cancel"
                width="145px"
                buttonType="secondary"
                onClick={handleCancel}
              >
                Cancel
              </AppButton>
            </Box>
            <Box margin={{ left: 'spacing6' }}>
              <AppButton testId="allow-roles_dropdown_apply" width="145px" onClick={handleApply}>
                Apply
              </AppButton>
            </Box>
          </Box>
        </Box>
      </Dropdown>
    </Box>
  );
};

export default AllowRolesDropdown;
